<template>
  <div class="choice-chip w-100">
    <label v-if="itemModel.vars.label" :class="'form-label' + (itemModel.vars.required ? ' required' : '')">
      {{ itemModel.vars.label }}{{ itemModel.vars.required ? "*" : "" }}
      <span
        v-if="itemModel.vars.help && !isMobile()"
        v-tooltip="{ title: itemModel.vars.help, textWrap: true }"
        class="form-info ms-1"
      >
        <FontAwesomeIcon icon="fa-light fa-circle-question" />
      </span>
    </label>
    <div v-if="isMobile() && itemModel.vars.help" class="form-info">
      <FontAwesomeIcon icon="fa-light fa-circle-question" />
      {{ itemModel.vars.help }}
    </div>
    <div
      class="choice-chip-group"
      :class="{ 'choice-chip-group--multiple': isMultiple }"
      role="group"
      :aria-label="itemModel.vars.label || null"
    >
      <template v-for="choice in choices" :key="choice.value">
        <span class="chip__field visually-hidden">
          <InputTypeComponent :model="chipModels[choice.value]" @input="select(choice)" />
        </span>
        <label :for="chipId(choice)" class="chip" :class="{ selected: isSelected(choice), muted: isMuted(choice) }">
          {{ choice.label }}
        </label>
      </template>
      <div v-if="itemModel.vars.error" class="invalid-feedback d-block">
        {{ itemModel.vars.error }}
      </div>
    </div>

    <BlockLoader :loading="!!itemModel.loading" />
  </div>
</template>

<script>
import InputTypeComponent from "@/components/form/InputTypeComponent.vue";
import BlockLoader from "@/components/blocks/BlockLoader.vue";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { formValidator } from "@/services/form/form-validator";
import { formManager } from "@/services/form/form-manager";
import { choiceIdentityManager } from "@/services/form/choice-identity-manager";

export default {
  name: "ChoiceChipTypeComponent",
  components: { InputTypeComponent, BlockLoader, FontAwesomeIcon },
  props: {
    model: {
      type: Object,
      required: true,
    },
  },
  emits: ["update", "ajaxTargetLoad", "ajaxTargetTrig", "resetInputs"],
  data() {
    return {
      itemModel: this.model,
      // Un modèle de case à cocher par chip (rendu par InputTypeComponent) : un clic sur une chip déjà
      // sélectionnée la désélectionne aussi en choix unique, ce qu'un bouton radio ne permet pas.
      chipModels: {},
    };
  },
  computed: {
    choices() {
      return Object.values(this.itemModel.vars.choices || []);
    },
    isMultiple() {
      return !!this.itemModel.vars.multiple;
    },
    selectedValues() {
      return this.itemModel.vars.value || [];
    },
    // Aligné sur CheckboxTypeComponent/SelectTypeComponent (revue Loïc, ST304-TA-4918) : readonly
    // et disabled désactivent tous deux les interactions.
    isDisabled() {
      return !!(this.itemModel.vars.disabled || (this.itemModel.vars.attr && this.itemModel.vars.attr.readonly));
    },
  },
  watch: {
    choices: {
      immediate: true,
      handler(choices) {
        this.chipModels = Object.fromEntries(choices.map((choice) => [choice.value, this.buildChipModel(choice)]));
      },
    },
    "itemModel.vars.value": {
      deep: true,
      handler() {
        this.choices.forEach((choice) => {
          if (this.chipModels[choice.value]) {
            this.chipModels[choice.value].vars.value = this.isSelected(choice);
          }
        });
      },
    },
    isDisabled(disabled) {
      Object.values(this.chipModels).forEach((chipModel) => {
        chipModel.vars.disabled = disabled;
      });
    },
  },
  created() {
    if (this.isMultiple && !this.itemModel.vars.value) {
      this.itemModel.vars.value = [];
    }
  },
  methods: {
    isMobile() {
      return window.innerWidth < 992;
    },
    buildChipModel(choice) {
      return {
        vars: {
          id: this.chipId(choice),
          name: this.itemModel.vars.name,
          value: this.isSelected(choice),
          required: false,
          disabled: this.isDisabled,
          label: "",
          block_prefixes: ["form", "checkbox"],
          attr: {},
        },
      };
    },
    chipId(choice) {
      return `${this.itemModel.vars.name}-chip-${choice.value}`;
    },
    matches(value, choice) {
      return choiceIdentityManager.identity(value) === choice.value;
    },
    isSelected(choice) {
      if (this.isMultiple) {
        return this.selectedValues.some((value) => this.matches(value, choice));
      }
      const value = this.itemModel.vars.value;
      if (value === null || value === undefined) {
        return false;
      }
      return this.matches(value, choice);
    },
    isMuted(choice) {
      return !!(choice.attr && choice.attr["data-type"] === "muted");
    },
    select(choice) {
      if (this.isMultiple) {
        this.itemModel.vars.value = this.isSelected(choice)
          ? this.selectedValues.filter((value) => !this.matches(value, choice))
          : [...this.selectedValues, choice.data];
      } else {
        this.itemModel.vars.value = this.isSelected(choice) ? null : choice.data;
      }
      formValidator.validModel(this.itemModel);
      this.$emit("update", this.itemModel.vars.value);

      if (this.itemModel.vars.attr && this.itemModel.vars.attr["data-ajax-load"]) {
        const ajaxTargets = this.itemModel.vars.attr["data-ajax-load"];
        if (
          this.itemModel.vars.attr["data-ajax-linked-to"] &&
          (this.itemModel.vars.attr["data-ajax-search"] || formManager.getAjaxIdentifier(this.itemModel))
        ) {
          this.$emit("ajaxTargetTrig", { trigger: this.itemModel });
        } else if (this.isMultiple) {
          const ids = this.selectedValues.map((value) => (typeof value === "object" ? value.id : value));
          this.loadMultipleAjaxTargets(ajaxTargets, ids);
        } else if (this.itemModel.vars.value !== null) {
          const id =
            typeof this.itemModel.vars.value === "object" ? this.itemModel.vars.value.id : this.itemModel.vars.value;
          formManager.fieldLoad(this.itemModel, this, id);
        }
        this.$emit("resetInputs", { item: ajaxTargets });
      }
    },
    async loadMultipleAjaxTargets(ajaxTargets, ids) {
      for (const target of Object.keys(ajaxTargets)) {
        if (!ids.length) {
          this.$emit("ajaxTargetLoad", { choices: {}, target });
          continue;
        }
        try {
          const results = await Promise.allSettled(ids.map((id) => formManager.fieldLoad(this.itemModel, null, id)));
          const merged = {};
          results.forEach((result) => {
            if (result.status === "fulfilled") {
              Object.assign(merged, result.value);
            }
          });
          this.$emit("ajaxTargetLoad", { choices: merged, target });
        } catch {
          this.$emit("ajaxTargetLoad", { choices: {}, target });
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "./choice-list-shared.scss";

.choice-chip {
  position: relative;
}

.choice-chip-group {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
}

.chip__field:has(:focus-visible) + .chip {
  outline: 2px solid $landing-green-dark;
  outline-offset: 1px;
}

.chip {
  @extend %choice-chip-base;
  transition: 0.15s;
  user-select: none;
  display: flex;
  border: 1.5px solid $landing-muted;
  background: $white;
  color: $landing-navy !important;

  &:hover {
    border-color: $landing-navy;
  }

  &.selected {
    background: $primary;
    border-color: $primary;
    color: $white !important;
  }

  &.muted {
    color: $landing-muted;
    border-style: dashed;

    &.selected {
      background: $landing-muted;
      border-color: $landing-muted;
      color: $white !important;
    }
  }
}

.invalid-feedback {
  margin-top: 6px;
  width: 100%;
}
</style>
